import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { tokenRequest, setToken } from '../redux/user';
import styles from './login.module.css';
import Button from '../components/Button';
import logo_albus from '@images/logo_albus_login.png';
import { crasapi } from 'app/store';
import QRCode from 'react-qr-code';
import {
  Alert,
  Box,
  List,
  ListItem,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

function LoginPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [polling, setPolling] = useState(false);
  const [bankIdData, setBankIdData] = useState(undefined);
  const [accounts, setAccounts] = useState(undefined);
  const [errMsg, setErrMsg] = useState(undefined);

  const onError = (err) => {
    setErrMsg(
      'Fel användarnamn/lösenord, alternativt har inloggning med bankid aktiverats.'
    );
  };
  const onSubmit = (data) => {
    dispatch(tokenRequest(data))
      .then((d) => console.log(d.payload.status))
      .catch(onError);
  };

  const onAccountSelect = useCallback(
    async (token) => {
      try {
        dispatch(setToken(token));
      } catch (error) {
        console.error('Error starting authentication2:', error);
        setAccounts(undefined);
      }
    },
    [dispatch]
  );

  const startAuth = async () => {
    try {
      const response = await crasapi.post(
        'bankid/start-auth',
        {
          useQrCode: true,
        },
        { withCredentials: true }
      );
      setPolling(true); // Start polling
    } catch (error) {
      console.error('Error starting authentication:', error);
      setPolling(false);
    }
  };

  useEffect(() => {
    const pollAuthStatus = async () => {
      try {
        const response = await crasapi.get(`bankid/collect-auth`, {
          withCredentials: true,
        });
        console.log(response.data);

        setBankIdData(response.data);
        if (response.data.resultCode === 'COMPLETE') {
          setPolling(false); // Stop polling if the transaction is completed or failed
          setBankIdData(undefined);
          //Only one account, log in direct.
          if (response.data?.accounts?.length === 1) {
            onAccountSelect(response.data.accounts[0].token);
          } else {
            setAccounts(response.data.accounts);
          }
        }
      } catch (error) {
        setBankIdData(undefined);
        setAccounts(undefined);
        console.error('Error polling authentication status:', error);
        setPolling(false);
      }
    };

    if (polling) {
      const intervalId = setInterval(pollAuthStatus, 1000); // Poll every second
      return () => clearInterval(intervalId); // Cleanup interval on component unmount or when polling stops
    }
  }, [onAccountSelect, polling]); // Effect runs when `polling` or `sessionId` changes

  return (
    <div className={styles.container}>
      <div className={styles.box}>
        <div className={styles.form}>
          <div className={styles.logo}>
            <img src={logo_albus} alt="logo" />
          </div>
          {!bankIdData && !accounts && (
            <>
              <form
                className={styles.forminput}
                onSubmit={handleSubmit(onSubmit)}
              >
                <input
                  type="text"
                  placeholder={t('userName')}
                  {...register('username', { required: true })}
                />
                {errors.username && errors.username.type === 'required' && (
                  <span>{t('required-field')}</span>
                )}
                <input
                  type="password"
                  placeholder={t('password')}
                  {...register('password', { required: true })}
                />
                {errors.password && errors.password.type === 'required' && (
                  <span>{t('required-field-password')}</span>
                )}
                <Button
                  className="mt-15px"
                  type="submit"
                  block={true}
                  color="primary"
                  size="big"
                >
                  {t('login')}
                </Button>
                {/* <div className={styles.txt_link + ' text-center mt-5'}>
              <Link to="/forgot-password">Glömt ditt lösenord?</Link>
            </div> */}
              </form>

              <Button
                className="mt-15px"
                type="submit"
                block={true}
                color="primary"
                size="big"
                onClick={startAuth}
              >
                BankID
              </Button>
            </>
          )}
          {bankIdData && bankIdData.qrData && (
            <div
              style={{
                height: 'auto',
                margin: '0 auto',
                maxWidth: '200px',
                width: '100%',
              }}
            >
              <QRCode
                size={256}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={bankIdData.qrData}
                viewBox={`0 0 256 256`}
              />
              <Typography align="center">
                {t(bankIdData.resultCode ?? '')}
              </Typography>
            </div>
          )}
          {accounts && (
            <Box>
              <Typography>{t('select-account-to-sign-in-with')}</Typography>
              <List>
                {accounts.map((x) => (
                  <ListItem key={x.userId} disablePadding>
                    <Button
                      onClick={() => onAccountSelect(x.token)}
                      className="mt-15px"
                      type="submit"
                      block={true}
                      color="primary"
                      size="big"
                    >
                      {x.firstName} {x.lastName} ({x.userName})
                    </Button>
                  </ListItem> // Assuming each account has a unique 'id'
                ))}
              </List>
            </Box>
          )}

          <p className={styles.txt}>
            Albus system är godkända medicintekniska produkter med CE-märkning.
            All data överförs krypterat och behandlas enligt
            personuppgiftslagen, patientdatalagen och EU:s dataskyddsförordning
            (GDPR).
          </p>

          <div className={styles.txt_link}>
            <a
              href="https://www.esante.se/sekretess-integritet"
              rel="noreferrer"
              target="_blank"
            >
              Läs mer om vår datahantering
            </a>
            <a
              style={{ paddingLeft: 30 }}
              href="https://www.albuscare.com/"
              rel="noreferrer"
              target="_blank"
            >
              Läs mer om Albus
            </a>
          </div>
        </div>
      </div>
      <Snackbar
        open={errMsg !== undefined}
        severity="error"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setErrMsg(undefined)}
      >
        <Alert
          onClose={() => setErrMsg(undefined)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default LoginPage;
